import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { OrgMemberRow } from 'pure-js/types/OrganizationMembership'
import { LEADER_SCOPE_MAX_USERS } from 'pure-js/libs/leaderInsights'

import { useLeaderScope, useSetLeaderScope } from '../../../../apiLayer/queryHooks/leader'
import { useReflectionScopeData } from '../../../../apiLayer/queryHooks/queries'
import { resolveLeaderErrorTranslationKey } from '../../../../apiLayer/processHandlers/leader'
import { filterMembersByQuery } from '../../helpers/orgFilters'
import { buildSprintGroups, buildTeamGroups, selectEligibleMembers } from './leaderScopeHelpers'

type UseLeaderScopeSelectionParams = {
  isOpen: boolean
  organizationId: string
  leader: OrgMemberRow | null
  members: OrgMemberRow[]
}

/** The ticks, the search, the quick-add groups and the save of the modal where a leader's people are chosen. */
export function useLeaderScopeSelection({ isOpen, organizationId, leader, members }: UseLeaderScopeSelectionParams) {
  const leaderUserId = leader?.userId

  const scopeQuery = useLeaderScope(organizationId, leaderUserId, { enabled: isOpen })
  const scopeTreeQuery = useReflectionScopeData({ enabled: isOpen })
  const { mutateAsync: saveScope, isPending: isSaving } = useSetLeaderScope()

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
  const [searchText, setSearchText] = useState('')
  const [isDirty, setIsDirty] = useState(false)
  const [errorTranslationKey, setErrorTranslationKey] = useState<string | undefined>(undefined)

  const eligibleMembers = useMemo(() => selectEligibleMembers(members, leaderUserId), [members, leaderUserId])

  const eligibleUserIds = useMemo(() => new Set(eligibleMembers.map((member) => member.userId)), [eligibleMembers])

  const shownMembers = useMemo(() => filterMembersByQuery(eligibleMembers, searchText), [eligibleMembers, searchText])

  // Teams and sprints are only a quick way to tick people; what is saved stays an explicit list.
  const sprints = scopeTreeQuery.data?.data

  const teamGroups = useMemo(() => buildTeamGroups(sprints ?? [], eligibleUserIds), [sprints, eligibleUserIds])

  const sprintGroups = useMemo(() => buildSprintGroups(sprints ?? [], eligibleUserIds), [sprints, eligibleUserIds])

  const savedUserIds = scopeQuery.data?.data?.scope?.userIds
  const hasLoadedSavedList = savedUserIds !== undefined
  const hasEligibleMembers = eligibleMembers.length > 0

  // Read through refs, so a later refetch of either list never undoes what was just ticked.
  const savedUserIdsRef = useRef(savedUserIds)
  savedUserIdsRef.current = savedUserIds

  const eligibleUserIdsRef = useRef(eligibleUserIds)
  eligibleUserIdsRef.current = eligibleUserIds

  // The modal opens on the saved people who are still here to be ticked.
  useEffect(() => {
    if (!isOpen) return

    const saved = savedUserIdsRef.current ?? []
    const stillHere = hasEligibleMembers ? saved.filter((userId) => eligibleUserIdsRef.current.has(userId)) : saved

    setSelectedUserIds(stillHere)
    setIsDirty(false)
    setErrorTranslationKey(undefined)
    setSearchText('')
  }, [isOpen, leaderUserId, hasLoadedSavedList, hasEligibleMembers])

  const addPeople = useCallback((userIds: string[]) => {
    setSelectedUserIds((current) => [...new Set([...current, ...userIds])])
    setIsDirty(true)
  }, [])

  const togglePerson = useCallback((userId: string) => {
    setSelectedUserIds((current) =>
      current.includes(userId) ? current.filter((selected) => selected !== userId) : [...current, userId]
    )
    setIsDirty(true)
  }, [])

  const selectAllShown = useCallback(
    () => addPeople(shownMembers.map((member) => member.userId)),
    [addPeople, shownMembers]
  )

  const clearSelection = useCallback(() => {
    setSelectedUserIds([])
    setIsDirty(true)
  }, [])

  const save = useCallback(async (): Promise<boolean> => {
    if (!leaderUserId) return false

    setErrorTranslationKey(undefined)

    try {
      await saveScope({ organizationId, leaderUserId, userIds: selectedUserIds })
      return true
    } catch (error) {
      setErrorTranslationKey(resolveLeaderErrorTranslationKey(error))
      return false
    }
  }, [leaderUserId, organizationId, selectedUserIds, saveScope])

  const isOverTheCap = selectedUserIds.length > LEADER_SCOPE_MAX_USERS

  return {
    selectedUserIds,
    shownMembers,
    hasEligibleMembers,
    teamGroups,
    sprintGroups,
    searchText,
    setSearchText,
    togglePerson,
    addPeople,
    selectAllShown,
    clearSelection,
    isLoading: scopeQuery.isLoading,
    hasLoadFailed: !!scopeQuery.error || scopeQuery.data?.success === false,
    isSaving,
    isSaveAllowed: isDirty && !isOverTheCap,
    isOverTheCap,
    maxUsers: LEADER_SCOPE_MAX_USERS,
    errorTranslationKey,
    save
  }
}
